import { Link, useParams } from "react-router-dom";
import { getNewsById, getRelatedNews, getMediaById } from "@/lib/data";
import { useSeo } from "@/hooks/useSeo";
import NewsCard from "@/components/feature/NewsCard";
import { typeLabels, typePaths } from "@/lib/labels";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;

  useSeo(
    item ? `${item.title} - 榴莲视频 - 影视资讯` : "影视资讯 - 榴莲视频",
    item ? item.summary.slice(0, 80) : "榴莲视频影视资讯详情页",
    item ? `榴莲视频,影视资讯,${item.tags.join(",")}` : "榴莲视频,影视资讯",
  );

  if (!item) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-200/70 text-foreground-300">
          <i className="ri-article-line text-4xl"></i>
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该资讯</h1>
        <p className="mt-2 text-sm text-foreground-400">文章可能已被移除或链接有误</p>
        <Link
          to="/news"
          className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = getRelatedNews(item.id, 3);
  const relatedMedia = getMediaById(item.relatedMediaId);

  return (
    <article className="mx-auto max-w-[900px] px-4 py-10 md:px-8 md:py-14">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-400">
        <Link to="/" className="hover:text-accent-500 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="hover:text-accent-500 cursor-pointer">影视资讯</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="dv-clamp-1 text-foreground-700">{item.title}</span>
      </nav>

      <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs text-background-50">{item.category}</span>
      <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
        {item.title}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-background-200/60 pb-5 text-sm text-foreground-300">
        <span className="flex items-center gap-1.5">
          <i className="ri-user-3-line"></i>
          {item.author}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-time-line"></i>
          发布于 {item.date}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-building-line"></i>
          {item.source}
        </span>
      </div>

      <div className="my-6 w-full overflow-hidden rounded-lg bg-background-200">
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title}-榴莲视频资讯`}
          className="h-full w-full object-cover"
        />
      </div>

      <div className="space-y-5 text-base leading-loose text-foreground-600">
        <p className="font-medium text-foreground-800">{item.summary}</p>
        {item.content.map((para, i) => (
          <div key={i}>
            <p>{para}</p>
            {i === 0 && (
              <div className="my-6 w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={item.inlineImage}
                  alt={`${item.title} 配图`}
                  title={`${item.title}-榴莲视频资讯配图`}
                  className="h-full w-full object-cover"
                />
                <p className="bg-background-100/60 px-4 py-2 text-center text-xs text-foreground-300">
                  图 / 榴莲视频影视编辑部（示意图）
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {item.tags.map((t) => (
          <span key={t} className="rounded-full bg-secondary-900/25 px-3 py-1 text-xs text-secondary-900">
            #{t}
          </span>
        ))}
      </div>

      {relatedMedia && (
        <Link
          to={`/detail/${relatedMedia.id}`}
          className="group mt-8 flex items-center gap-4 rounded-lg border border-background-200/60 bg-background-100/50 p-4 transition-colors hover:border-accent-500/60 cursor-pointer"
        >
          <span className="w-16 h-24 shrink-0 overflow-hidden rounded-md bg-background-200">
            <img
              src={relatedMedia.poster}
              alt={relatedMedia.title}
              title={`${relatedMedia.title}-${typeLabels[relatedMedia.type]}-榴莲视频`}
              className="h-full w-full object-top"
            />
          </span>
          <div className="min-w-0">
            <p className="text-xs text-foreground-300">文中提及</p>
            <p className="mt-1 text-base font-medium text-foreground-950 transition-colors group-hover:text-accent-500">
              {relatedMedia.title}
            </p>
            <p className="mt-1 dv-clamp-1 text-sm text-foreground-400">
              {typeLabels[relatedMedia.type]} · {relatedMedia.year} · {relatedMedia.genres.join(" / ")}
            </p>
          </div>
          <i className="ri-arrow-right-line ml-auto text-lg text-foreground-300 transition-colors group-hover:text-accent-500"></i>
        </Link>
      )}

      <div className="mt-10 border-t border-background-200/60 pt-6">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-500 transition-colors hover:border-accent-500 hover:text-accent-500 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>

      <section className="mt-14">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <i className="ri-links-line text-accent-500"></i>
          相关资讯
        </h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((n) => (
            <NewsCard key={n.id} item={n} />
          ))}
        </div>
      </section>
    </article>
  );
}